import { ChartDataset } from 'chart.js';
import { BarChartMetaData } from '../../shared/types/widget';
import { getLabelAndColor } from './utils';

// Creates or reuses a tooltip container DOM element inside chart's parent node
export const createTooltip = (chart: any, tooltipId: string) => {
  let tooltipEl = chart.canvas.parentNode.querySelector(`.${tooltipId}`);
  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.classList.add(tooltipId);

    const tooltipDiv = document.createElement('div');
    tooltipDiv.classList.add('chartjs-tooltip');
    tooltipEl.appendChild(tooltipDiv);

    chart.canvas.parentNode.appendChild(tooltipEl);
  }
  return tooltipEl;
};

// Custom tooltip positioning and rendering
export const tooltipHandler = (
  context: any,
  tooltipContent: HTMLElement | null = null,
  tooltipId = 'chartjsTooltip-container',
) => {
  const { chart, tooltip } = context;
  const tooltipEl = createTooltip(chart, tooltipId);
  if (!tooltipEl) return;

  // Hide tooltip when there's no data or it's not visible
  if (!tooltip.body || tooltip.body.length === 0 || tooltip.opacity === 0) {
    tooltipEl.style.opacity = '0';
    tooltipEl.style.display = 'none';
    return;
  }

  // Get canvas position to calculate tooltip placement
  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
  const container = tooltipEl.firstElementChild;
  if (!container) return;

  // Clear previous tooltip content
  while (container.firstChild) container.removeChild(container.firstChild);

  // Inject new tooltip DOM content
  if (tooltipContent) {
    container.appendChild(tooltipContent);
  }

  // Position and style tooltip
  tooltipEl.style.display = 'block';
  tooltipEl.style.opacity = '1';
  tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
  tooltipEl.style.top = `${positionY + tooltip.caretY}px`;
  tooltipEl.style.font = tooltip.options.bodyFont.string;
  tooltipEl.style.padding = `${tooltip.options.padding}px`;
};

export function getProcessedMeta({
  meta,
  additionalDatasets = [],
  headCount,
  respondents,
}: {
  meta: BarChartMetaData;
  additionalDatasets?: ChartDataset<'bar' | 'line'>[];
  headCount?: number;
  respondents?: number[];
}) {
  const getBackgroundColor = (score: number, respondent: number) => {
    const colorObj = getLabelAndColor(Math.round(score), scoreRanges);
    const baseColor = colorObj?.color;
    const dimmed = headCount !== undefined && respondent < headCount;
    return dimmed ? `${baseColor}80` : baseColor;
  };
  // Return meta as-is if invalid or missing datasets
  if (!meta?.data || !Array.isArray(meta?.data?.datasets)) return meta;

  // Extract settings
  const scoreRanges = meta.meta?.scoreRanges || [];
  const isScoreRangeMode = meta.meta?.colorMode === 'scoreRange';
  const isHorizontal = meta.meta?.indexAxis === 'y';
  const isDimensionURL =
    typeof window !== 'undefined' &&
    window.location.href.includes('dimensions');

  // Process each dataset
  const updatedDatasets: ChartDataset<'bar'>[] = meta.data.datasets.map(
    (dataset, index) => {
      const data = Array.isArray(dataset.data) ? dataset.data : [];

      const scoreData = data.map((val) =>
        typeof val === 'number' ? Math.round(val) : val,
      );

      const respondentsData = respondents ?? [];
      const barData = scoreData.map((score, idx) => ({
        score,
        respondent: respondentsData[idx] ?? 0,
      }));

      const useScoreRangeColor =
        isScoreRangeMode || !meta.meta?.backgroundColors?.[index];
      const colorBase = meta.meta?.backgroundColors?.[index];

      let backgroundColor: string[] | string;

      // Curve line chart if scoreRange present in meta
      if (useScoreRangeColor) {
        backgroundColor = barData
          .map(({ score, respondent }) => {
            return getBackgroundColor(score, respondent);
          })
          .filter(Boolean) as string[];
      }

      // Horizontal bar chart
      else if (isHorizontal) {
        backgroundColor = [colorBase];
      }
      // Vertical bar chart
      else {
        backgroundColor = scoreData.map((_, idx) =>
          idx === scoreData.length - 1 && !isDimensionURL
            ? `${colorBase}80`
            : colorBase,
        );
      }

      return {
        ...dataset,
        data: scoreData,
        type: 'bar',
        yAxisID: 'y',
        backgroundColor,
        legendColor: useScoreRangeColor
          ? getLabelAndColor(scoreData[scoreData.length - 1] || 0, scoreRanges)
              ?.color
          : colorBase,
        ...(isHorizontal && {
          borderWidth: 2,
          borderSkipped: false,
          ...(useScoreRangeColor
            ? {}
            : { borderColor: ['rgba(255, 255, 255, 1)'] }),
        }),
      };
    },
  );

  return {
    ...meta,
    data: {
      ...meta.data,
      datasets: [...updatedDatasets, ...(additionalDatasets || [])],
    },
  } as BarChartMetaData;
}
